'use client';

import { createBrowserClient } from '@supabase/ssr';

// Singleton — one client instance across the entire app lifetime
let _client: ReturnType<typeof createBrowserClient> | null = null;

// Deduplication map: in-flight refresh requests keyed by URL+body
const _inflight = new Map<string, Promise<Response>>();

/**
 * Custom fetch that:
 * 1. Deduplicates concurrent refresh_token requests (multiple tabs / rapid navigation)
 * 2. Retries with exponential backoff on 429 rate-limit responses
 */
async function dedupedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  const isRefresh = url.includes('grant_type=refresh_token') || (typeof init?.body === 'string' && init.body.includes('grant_type=refresh_token'));

  if (isRefresh) {
    const key = url + (typeof init?.body === 'string' ? init.body : '');
    const existing = _inflight.get(key);
    if (existing) {
      // Return a clone of the in-flight response so each caller gets its own readable stream
      return existing.then((r) => r.clone());
    }

    const promise = fetchWithBackoff(input, init).finally(() => {
      _inflight.delete(key);
    });
    _inflight.set(key, promise);
    return promise;
  }

  return fetch(input, init);
}

async function fetchWithBackoff(input: RequestInfo | URL, init?: RequestInit, attempt = 0): Promise<Response> {
  const response = await fetch(input, init);

  if (response.status === 429 && attempt < 4) {
    // Exponential backoff: 1s, 2s, 4s, 8s
    const delay = Math.pow(2, attempt) * 1000;
    await new Promise((resolve) => setTimeout(resolve, delay));
    return fetchWithBackoff(input, init, attempt + 1);
  }

  return response;
}

export function createClient() {
  if (_client) return _client;

  _client = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      auth: {
        // Keep auto-refresh enabled but persist session to localStorage
        // so the token is reused across tabs rather than each tab refreshing independently
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        // Refresh the token 10 minutes before expiry (default is 60s)
        // This gives a wider window and reduces the chance of simultaneous expiry across tabs
      },
      global: {
        fetch: dedupedFetch,
      },
    }
  );

  return _client;
}
